import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import {
  Formik,
  Field as FormikField,
  FieldArray as FormikFieldArray,
  Form as FormikForm
} from 'formik';
import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import { Button } from 'lucid-components-react';
import * as queryString from 'query-string';
import React, { FC, ReactNode, useEffect, useState } from 'react';

import { useCookie } from '../../hooks/use-cookie';
import { ThemeProps } from '../../theme/theme';
import {
  TrackerFactory,
  TrackerType
} from '../../utils/events/tracker-factory';
import i18nFunct from '../../utils/i18n';
import { localizePhoneNumbers } from '../../utils/localizePhoneNumbers';

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

const GRECAPTCHA_SITE_KEY = '6LdYE8EZAAAAALwdjjrkN3IOxpdhXR5LdGsBxUw8';
const GRECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?render=${GRECAPTCHA_SITE_KEY}`;

type InputType =
  | 'text'
  | 'number'
  | 'telephone'
  | 'email'
  | 'htmltext'
  | 'select'
  | 'checkboxes'
  | 'textArea'
  | 'hidden'
  | 'fieldset';

interface InputField {
  rowNumber?: number;
  columnNumber?: number;
  dataType: InputType;
  label: string;
  marketoId: string;
  required: boolean;
  defaultValue?: string;
  text?: string;
  formPrefill?: boolean;
  fieldMetaData: {
    values: Array<{
      label: string;
      value: string;
    }>;
  };
  visibilityRules: {
    ruleType: string;
    rules: Array<{
      subjectField: string;
      operator: string;
      values: string[];
      altLabel: string;
      picklistFilterValues: Array<{
        label: string;
        value: string;
        selected: boolean;
      }>;
    }>;
  };
  autoFill: {
    value: string;
    valueFrom: string;
    parameterName: string;
  };
}

export interface MarketoFormInterface {
  name?: string | null;
  marketoId?: string | null;
  description?: string | null;
  childrenMarketoFormField?: Array<InputField>;
}

export interface MarketoFormData {
  form: MarketoFormInterface | null;
  topText: {};
  confirm: {};
  submitText?: string;
}

export interface Props {
  form: MarketoFormInterface;
  topText?: {
    title?: string;
    body?: string;
    note?: string;
  };
  confirm?: {
    heading?: string;
    body?: string;
  };
  submitText?: string | null;
  showLabels?: boolean;
  fullWidth?: boolean;
  fullWidthSubmit?: boolean;
  leftAlignSubmit?: boolean;
  showTerms?: boolean;
  hideTopText?: boolean;
  location: {
    pathname?: string;
    origin?: string;
    search?: string;
  };
  i18nKey?: string;
  children?: ReactNode;
  onSuccess?: () => void;
  useReferrer?: boolean;
  variant?: 'old' | 'press';
}

interface LabelProps {
  fullWidth: boolean;
  variant?: string;
}

interface FieldsetProps {
  isRealFieldset: boolean;
}

interface InputProps {
  variant?: string;
}

interface SubmitWrapperProps {
  leftAlignSubmit?: boolean;
}

const Form = styled
  .form<ThemeProps>(({ theme }) => ({
    fontSize: theme.fontSizes[3]
  }))
  .withComponent(FormikForm);

const TopText = styled.div<InputProps & Partial<ThemeProps>>(
  {
    flex: '0 1 100%'
  },
  ({ variant }) => ({
    marginBottom: variant === 'press' ? '1.25rem' : '2rem'
  })
);

const TopTitle = styled.h3<InputProps & Partial<ThemeProps>>(
  {
    margin: 0,
    marginBottom: '1rem'
  },
  ({ theme, variant }) => {
    const styles = {
      fontSize: theme.fontSizes[3],
      lineHeight: theme.lineHeights.heading,
      fontWeight: theme.fontWeights.heading
    } as { [key: string]: string | number };

    if (variant === 'press') {
      styles.fontSize = '2rem';
      styles.backgroundColor = '#FFFFFF';
      styles.color = '#000000';
      styles.fontWeight = '600';
      styles.margin = 0;
      styles.whiteSpace = 'pre-line';
    }

    return styles;
  }
);

const Body = styled.div<Partial<ThemeProps>>(
  {
    fontSize: '1rem',
    margin: '0 0 1rem'
  },
  ({ theme }) => ({
    fontSize: theme.fontSizes[3],
    lineHeight: theme.lineHeights.body
  })
);

const Note = styled.div<Partial<ThemeProps>>(
  {
    margin: '0 0 2rem'
  },
  ({ theme }) => ({
    fontSize: theme.fontSizes[3],
    lineHeight: theme.lineHeights.body
  })
);

const Terms = styled.div<InputProps & Partial<ThemeProps>>(
  {
    a: {
      textDecoration: 'underline'
    }
  },
  ({ theme, variant }) => ({
    margin: variant === 'press' ? '1rem 0 0' : '1rem 0',
    fontSize: variant === 'press' ? '0.75rem' : '0.875rem',
    color: variant === 'press' ? '#76818C' : theme.colors.text,
    a: {
      color: variant === 'press' ? theme.colors.primary : theme.colors.text,
      fontWeight: theme.fontWeights.body,
      '&:visited, &:hover': {
        color: variant === 'press' ? theme.colors.primary : theme.colors.text
      }
    }
  })
);

const Fieldset = styled.div<FieldsetProps & Partial<ThemeProps>>(
  ({ isRealFieldset }) => ({
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    ...(isRealFieldset && { marginBottom: '2rem' })
  })
);

const Legend = styled.legend<Partial<ThemeProps>>(
  { marginBottom: '1rem' },
  ({ theme }) => ({
    fontSize: theme.fontSizes[4],
    fontWeight: theme.fontWeights.bold
  })
);

const Input = styled.input<InputProps & Partial<ThemeProps>>(
  ({ theme, variant }) => ({
    width: '100%',
    marginTop: '4px',
    padding: '10px',
    background: 'transparent',
    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
    appearance: 'none',
    backgroundColor: '#FFFFFF',
    'select&': {
      backgroundImage:
        "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMS4xOCA2LjM5Ij48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6IzJjMzYzZjt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmNhcmV0PC90aXRsZT48ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIj48ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPjxwb2x5Z29uIGNsYXNzPSJjbHMtMSIgcG9pbnRzPSI1LjU5IDYuMzkgMTEuMTggMCAwIDAgNS41OSA2LjM5Ii8+PC9nPjwvZz48L3N2Zz4=')",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '0.8rem',
      '&:invalid': {
        color: variant === 'press' ? theme.colors.disabledBorder : '#CCCCCC',
        fontSize: variant === 'press' ? theme.fontSizes[2] : 'inherit',
        lineHeight: variant === 'press' ? '1.5' : 'inherit'
      }
    },
    'textarea&': {
      minHeight: variant === 'press' ? '5rem' : 'unset'
    }
  }),
  ({ theme, variant }) => {
    let borderColor = theme.colors.primary;

    if (variant === 'old') {
      borderColor = '#CCCCCC';
    }
    if (variant === 'press') {
      borderColor = theme.colors.disabledBorder;
    }

    let focusColor = '#F88F21';
    if (variant === 'press') {
      focusColor = theme.colors.secondary;
    }
    return {
      '&:focus': {
        outline: 'none',
        border: `1px solid ${focusColor}`
      },
      border: `1px solid ${borderColor}`,
      borderRadius:
        variant === 'old' || variant === 'press' ? '3px' : theme.radii.button,
      fontSize: theme.fontSizes[3],
      lineHeight: theme.lineHeights.body,
      '&::placeholder': {
        color: theme.colors.disabledBorder,
        fontSize: variant === 'press' ? theme.fontSizes[2] : 'inherit'
      }
    };
  }
);

const CheckboxGroup = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: '1.2rem',
  flexDirection: 'column'
});

const CheckboxLabel = styled.label<LabelProps & Partial<ThemeProps>>(
  {
    position: 'relative',
    flex: '0 1 auto',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: '0.5rem',
    minHeight: '30px'
  },
  ({ fullWidth, theme, variant }) => ({
    flex: fullWidth ? '0 1 100%' : '0 1 49%',
    fontSize:
      variant === 'old' || variant === 'press'
        ? '0.8125rem'
        : theme.fontSizes[3],
    fontWeight: variant === 'press' ? 200 : theme.fontWeights.body,
    [`@media (max-width: ${theme.breakpoints[1]})`]: {
      flex: '0 1 100%'
    }
  })
);

const Checkbox = styled.input<InputProps & Partial<ThemeProps>>(
  {
    display: 'none'
  },
  ({ theme, variant }) => {
    let borderColor = theme.colors.primary;

    if (variant === 'old') {
      borderColor = '#CCCCCC';
    }
    if (variant === 'press') {
      borderColor = '#0C102C';
    }

    let focusColor = '#F88F21';
    if (variant === 'press') {
      focusColor = theme.colors.primary;
    }
    return {
      '&:focus': {
        outline: 'none',
        '+ span::before': {
          border: `1px solid ${focusColor}`
        }
      },
      '&:checked': {
        outline: 'none',
        '+ span::before': {
          content: '"\\2713"',
          backgroundColor: theme.colors.primary
        }
      },
      '+ span': {
        paddingLeft: '40px',
        lineHeight: '1'
      },
      '+ span::before': {
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        width: '30px',
        height: '30px',
        fontWeight: theme.fontWeights.heading,
        padding: '5px 0 0 6px',
        borderRadius:
          variant === 'old' || variant === 'press' ? '2px' : theme.radii.button,
        display: 'block',
        color: theme.colors.white,
        border: `1px solid ${borderColor}`,
        transition: 'background-color 0.2s ease-out'
      }
    };
  }
);

const TextArea = Input.withComponent('textarea');
const Field = Input.withComponent(FormikField);

const Label = styled.label<LabelProps & Partial<ThemeProps>>(
  {
    marginBottom: '1.2rem',
    position: 'relative',
    display: 'block'
  },
  ({ fullWidth, theme, variant }) => ({
    flex: fullWidth ? '0 1 100%' : '0 1 49%',
    fontSize:
      variant === 'old' || variant === 'press'
        ? '0.8125rem'
        : theme.fontSizes[3],
    fontWeight: variant === 'press' ? 200 : theme.fontWeights.heading,
    [`@media (max-width: ${theme.breakpoints[1]})`]: {
      flex: '0 1 100%'
    }
  })
);

const SubmitWrapper = styled.div<SubmitWrapperProps>(({ leftAlignSubmit }) => ({
  flex: '0 1 100%',
  textAlign: leftAlignSubmit ? 'left' : 'center'
}));

const RequiredContainer = styled.span({
  color: '#BF0000',
  marginRight: '3px'
});

const ConfirmHeader = styled.h3<InputProps & Partial<ThemeProps>>(
  {
    flex: '0 1 100%'
  },
  ({ theme, variant }) => ({
    fontSize: theme.fontSizes[7],
    textAlign: variant === 'old' ? 'center' : 'left'
  })
);

const ConfirmBody = styled.div<InputProps & Partial<ThemeProps>>(
  {
    flex: '0 1 100%'
  },
  ({ theme, variant }) => ({
    fontSize: theme.fontSizes[3],
    textAlign: variant === 'old' ? 'center' : 'left'
  })
);

const MarketoForm: FC<Props> = ({
  form,
  topText = {},
  confirm = {},
  submitText = null,
  showLabels = true,
  fullWidth = false,
  fullWidthSubmit = false,
  leftAlignSubmit = false,
  showTerms = true,
  hideTopText = false,
  location,
  i18nKey = 'en',
  children = null,
  onSuccess = null,
  useReferrer = false,
  variant
}) => {
  const tracker = TrackerFactory.create(TrackerType.Beacon);
  const kmTracker = TrackerFactory.create(TrackerType.Kissmetrics);
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState(false);
  const { title, body, note } = topText || {};
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_all_default_form_heading
                mktg_ct_all_default_form_thankyou_message
                mktg_mkto_fail_header
                mktg_mkto_fail_adblocker
                mktg_mkto_fail_problem
                mtkg_submit_policy
                mktg_generics_forms_submit
              }
            }
          }
        }
      }
    `
  );

  const formId = form.marketoId;

  const typeLookup: { [key: string]: string } = {
    text: 'text',
    telephone: 'tel',
    email: 'email',
    number: 'number',
    currency: 'text'
  };
  const parsedQueryString = queryString.parse(location.search || '');
  const validDatatypes = [
    'textArea',
    'text',
    'number',
    'currency',
    'telephone',
    'email',
    'select',
    'checkboxes',
    'hidden'
  ];
  const overrideTopText = hideTopText || title || body || note;

  if (!overrideTopText) {
    validDatatypes.push('htmltext');
  }

  const textAreaIds: string[] = [];
  const i18n = i18nFunct(i18nData, i18nKey);
  const htmlTags = /<\/?[^>]+(>|$)/g;

  const groupedFields = form
    // fieldsets are added after the fields that they hold
    // so we reduceRight to get the fieldsets and group their
    // children within
    .childrenMarketoFormField!.reduceRight(
      (groups, child) => {
        if (child.dataType === 'fieldset') {
          if (!groups[0].fieldsetTitle) {
            groups[0].fieldsetTitle = child.label;
          } else {
            groups.push({ fieldsetTitle: child.label, fields: [] });
          }
        } else if (validDatatypes.includes(child.dataType)) {
          const groupIndex = groups.length - 1;
          // unshift to build the array from the end to the start
          // as that is the order we get the fields using reduceRight
          groups[groupIndex].fields.unshift(child);
        }
        return groups;
      },
      [{ fieldsetTitle: undefined, fields: [] }] as Array<{
        fieldsetTitle?: string;
        fields: InputField[];
      }>
    )
    // because we used reduceRight we have to then reverse the array
    // to get the correct order of the fieldsets and fields
    .reverse();

  const emptyValues: Record<string, string> = {};
  groupedFields.forEach(group => {
    group.fields.forEach(field => (emptyValues[field.marketoId] = ''));
  });

  const sessionKey = `mkto-${formId}`;

  let sessionValues = null;

  if (typeof window !== 'undefined') {
    // Try-Catch incase the session storage returns malformed data
    try {
      sessionValues = JSON.parse(sessionStorage.getItem(sessionKey) || '');
    } catch {
      // no-op
    }
  }

  const initialValues = sessionValues || emptyValues;

  const [marketoTracker] = useCookie('_mkto_trk');
  const [userId] = useCookie('userId');

  const hiddenFields: Record<string, string> = {};

  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    tracker.report({
      name: 'openedMarketoForm',
      formId,
      formName: form.name || '',
      userId: Number(userId) || -1,
      anonymousId: Cookies.get('lt-anonymous-id') || '',
      previousPageviewId: sessionStorage.getItem('previousPageviewId') || null
    });

    if (document.querySelector(`script[src="${GRECAPTCHA_SRC}"]`)) {
      window.grecaptcha?.ready(() => {
        setRecaptchaLoaded(true);
      });
    }
    const recaptchaScript = document.createElement('script');
    recaptchaScript.src = GRECAPTCHA_SRC;
    recaptchaScript.addEventListener('load', () => {
      window.grecaptcha?.ready(() => {
        setRecaptchaLoaded(true);
      });
    });
    document.getElementsByTagName('head')[0].appendChild(recaptchaScript);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (formSent) {
    if (onSuccess) {
      onSuccess();
    }
    return (
      <>
        <div>
          <ConfirmHeader variant={variant}>
            {confirm.heading || i18n('mktg-ct-all-default-form-heading')}
          </ConfirmHeader>
          <ConfirmBody
            dangerouslySetInnerHTML={{
              __html:
                confirm.body ||
                i18n('mktg-ct-all-default-form-thankyou-message')
            }}
          />
        </div>
        {children && children}
      </>
    );
  }

  if (formError) {
    return (
      <div>
        <ConfirmHeader>{i18n('mktg-mkto-fail-header')}</ConfirmHeader>
        <ConfirmBody>{i18n('mktg-mkto-fail-adblocker')}</ConfirmBody>
        <ConfirmBody>{i18n('mktg-mkto-fail-problem')}</ConfirmBody>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);

        const formData = new FormData();
        const hiddenValues: Record<string, string> = {
          Attribution_URL__c: useReferrer
            ? sessionStorage.getItem('prevLocation') || 'no referrer provided'
            : window.location.href,
          Attribution_URL_Path__c: window.location.pathname,
          _mkto_trk: marketoTracker || '',
          formid: formId || '',
          formVid: formId || '',
          munchkinId: '805-PTZ-019',
          hasMoreThanTenCharacters: ''
        };
        if (textAreaIds.length > 0) {
          hiddenValues.hasMoreThanTenCharacters = textAreaIds.some(
            id => values[id]?.length > 10
          )
            ? 'yes'
            : 'no';
        }
        Object.keys(hiddenFields).forEach(key => {
          if (hiddenFields[key]) {
            hiddenValues[key] = hiddenFields[key];
          }
        });
        Object.keys(hiddenValues).forEach(key => {
          if (hiddenValues[key]) {
            formData.append(key, hiddenValues[key]);
          }
        });
        Object.keys(values).forEach(key => {
          if (values[key]) {
            formData.append(key, values[key]);
          }
        });
        sessionStorage.removeItem(sessionKey);
        const request = new XMLHttpRequest();
        request.addEventListener('load', () => {
          // Send event to Tag Manager
          // @ts-ignore
          (window.dataLayer || []).push({
            event: 'mktoFormSubmit',
            marketo_formId: formId
          });

          // Send event to Beacon
          tracker.report({
            name: 'submittedMarketoForm',
            formId,
            formName: form.name || '',
            userId: Number(userId) || -1,
            anonymousId: Cookies.get('lt-anonymous-id') || '',
            previousPageviewId:
              sessionStorage.getItem('previousPageviewId') || null
          });

          // Special case for Press to finish some KM testing
          // before we move to Beacon exclusively

          // Send event to KissMetrics
          kmTracker.report({
            name: 'Marketo form submitted',
            value: {
              url: window.location.pathname,
              formId,
              formName: form.name || '',
              userId: Number(userId) || -1,
              anonymousId: Cookies.get('lt-anonymous-id') || '',
              previousPageviewId:
                sessionStorage.getItem('previousPageviewId') || null
            }
          });
          setFormSent(true);
        });
        request.addEventListener('error', () => {
          setFormError(true);
          sessionStorage.setItem(sessionKey, JSON.stringify(values));
        });
        request.open(
          'POST',
          'https://app-ab23.marketo.com/index.php/leadCapture/save2'
        );

        try {
          const token = await window.grecaptcha?.execute(GRECAPTCHA_SITE_KEY, {
            action: 'submit'
          });
          if (token) {
            formData.append('grecaptchaToken', token);
          }
        } catch (err) {
          console.log(err);
        }

        request.send(formData);
      }}
      validateOnChange={false}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form data-form-id={formId} method="post">
          {overrideTopText && !hideTopText && (
            <TopText variant={variant}>
              {title && <TopTitle variant={variant}>{title}</TopTitle>}
              {body && (
                <Body
                  className="js-phone-number-children-async"
                  dangerouslySetInnerHTML={{
                    __html: localizePhoneNumbers(body)
                  }}
                />
              )}
              {note && (
                <Note
                  dangerouslySetInnerHTML={{
                    __html: note
                  }}
                />
              )}
            </TopText>
          )}
          {groupedFields.map(group => {
            return (
              <Fieldset
                key={group.fieldsetTitle || 'default-group'}
                className="marketo-form__fieldset"
                isRealFieldset={group.fieldsetTitle ? true : false}
              >
                {group.fieldsetTitle && <Legend>{group.fieldsetTitle}</Legend>}
                {group.fields.map(child => {
                  const {
                    dataType,
                    marketoId,
                    label,
                    fieldMetaData,
                    visibilityRules,
                    autoFill
                  } = child;

                  let visibilityFilterValues;
                  const { ruleType, rules } = visibilityRules || {};

                  if (ruleType === 'show' || ruleType === 'hide') {
                    const matchFound = rules.find(
                      ({
                        subjectField,
                        operator,
                        values: ruleValues,
                        picklistFilterValues
                      }) => {
                        let keep = true;
                        visibilityFilterValues = picklistFilterValues;
                        if (operator === 'contains') {
                          keep = ruleValues.some(
                            ruleValue =>
                              values[subjectField].indexOf(ruleValue) !== -1
                          );
                        } else if (operator === 'is') {
                          keep = ruleValues.some(
                            ruleValue => values[subjectField] === ruleValue
                          );
                        } else if (operator === 'isNot') {
                          keep = ruleValues.some(
                            ruleValue => values[subjectField] !== ruleValue
                          );
                        }

                        return keep;
                      }
                    );

                    if (
                      (ruleType === 'show' && !matchFound) ||
                      (ruleType === 'hide' && matchFound)
                    ) {
                      delete values[marketoId];

                      return undefined;
                    }
                  }
                  const labelString = label || '';
                  const updatedLabel = (labelString.endsWith(':')
                    ? labelString.substring(0, labelString.length - 1)
                    : labelString
                  ).replace(htmlTags, '');

                  if (dataType === 'htmltext') {
                    return (
                      <TopText
                        key={marketoId}
                        className="js-phone-number-children-async"
                        dangerouslySetInnerHTML={{
                          __html: localizePhoneNumbers(child.text || '')
                        }}
                        variant={variant}
                      />
                    );
                  }

                  if (dataType === 'textArea') {
                    textAreaIds.push(marketoId);
                  }

                  if (dataType === 'checkboxes') {
                    const boxes = fieldMetaData.values;
                    return (
                      <FormikFieldArray
                        name={marketoId}
                        render={arrayHelpers => (
                          <>
                            <Label fullWidth={fullWidth} variant={variant}>
                              {updatedLabel}
                            </Label>
                            <CheckboxGroup>
                              {boxes.map(box => (
                                <CheckboxLabel
                                  key={box.label}
                                  fullWidth={fullWidth}
                                  variant={variant}
                                >
                                  <Checkbox
                                    checked={values[marketoId].includes(
                                      box.value
                                    )}
                                    name={marketoId}
                                    onChange={e => {
                                      if (e.target.checked)
                                        arrayHelpers.push(box.value);
                                      else {
                                        const idx = values[marketoId].indexOf(
                                          box.value
                                        );
                                        arrayHelpers.remove(idx);
                                      }
                                    }}
                                    type="checkbox"
                                    value={box.value}
                                    variant={variant}
                                  />

                                  <span>{box.label}</span>
                                </CheckboxLabel>
                              ))}
                            </CheckboxGroup>
                          </>
                        )}
                      />
                    );
                  }

                  if (dataType === 'hidden') {
                    const { value, valueFrom, parameterName } = autoFill;
                    let finalValue = '';
                    if (valueFrom === 'default') {
                      finalValue = value;
                    } else if (valueFrom === 'cookie') {
                      finalValue = Cookies.get(parameterName) || '';
                    } else if (valueFrom === 'query') {
                      finalValue = parsedQueryString[parameterName] as string;
                    }
                    hiddenFields[marketoId] = finalValue;
                    return (
                      <Field
                        key={marketoId}
                        name={marketoId}
                        type="hidden"
                        value={finalValue}
                      />
                    );
                  }

                  return (
                    <Label
                      key={marketoId}
                      fullWidth={
                        variant === 'press' && dataType === 'textArea'
                          ? true
                          : fullWidth
                      }
                      variant={variant}
                    >
                      {showLabels && (
                        <>
                          {child.required && (
                            <RequiredContainer>*</RequiredContainer>
                          )}
                          {updatedLabel}
                        </>
                      )}

                      {typeLookup[dataType] && (
                        <Input
                          min="0"
                          name={marketoId}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setFieldValue(marketoId, e.target.value)}
                          placeholder={updatedLabel}
                          required={child.required}
                          type={typeLookup[dataType]}
                          value={values[marketoId]}
                          variant={variant}
                        />
                      )}
                      {dataType === 'select' && (
                        <Field
                          component="select"
                          name={marketoId}
                          required={child.required}
                          variant={variant}
                        >
                          {(visibilityFilterValues || fieldMetaData.values).map(
                            ({ label: optLabel, value }) => {
                              if (!value && !showLabels) {
                                return (
                                  <option
                                    key={value}
                                    disabled
                                    aria-selected="false"
                                    value={value}
                                  >
                                    {updatedLabel}
                                  </option>
                                );
                              }
                              return (
                                <option
                                  key={value}
                                  aria-selected="false"
                                  value={value}
                                >
                                  {optLabel}
                                </option>
                              );
                            }
                          )}
                        </Field>
                      )}
                      {dataType === 'textArea' && (
                        <TextArea
                          name={marketoId}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                          ) => setFieldValue(marketoId, e.target.value)}
                          placeholder={updatedLabel}
                          required={child.required}
                          value={values[marketoId]}
                          variant={variant}
                        />
                      )}
                    </Label>
                  );
                })}
              </Fieldset>
            );
          })}

          <SubmitWrapper leftAlignSubmit={leftAlignSubmit}>
            <Button
              primary
              block={fullWidthSubmit}
              disabled={!recaptchaLoaded || isSubmitting}
              size="large"
              type="submit"
            >
              {submitText || i18n('mktg-generics-forms-submit')}
            </Button>
          </SubmitWrapper>
          {showTerms && (
            <Terms
              dangerouslySetInnerHTML={{
                __html: i18n('mtkg-submit-policy', {}, [
                  '<a href="/pages/privacy" target="_blank" rel="noopener noreferrer">{}</a>'
                ])
              }}
              variant={variant}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default withTheme(MarketoForm);
